/**
 * Studio: the Channels page (Settings): web chat, and the email, WhatsApp and Telegram inboxes of
 * a brand's Chatwoot account, with whether the AI answers there, their team, and pausing them.
 */
export const channelsEn = {
  nav: 'Channels',
  title: 'Channels',
  description: 'Answer email, WhatsApp and Telegram from the same inbox as your web chat.',
  loading: 'Loading channels',
  guide: 'How channels work',
  brand: 'Brand',
  list: 'Channels of {{brand}}',
  forbidden: {
    title: 'You can’t manage channels',
    description: 'Ask a workspace owner or administrator for a role that may manage integrations.',
  },
  noBrands: {
    title: 'No brand yet',
    description: 'Add a brand first. Then connect its channels here.',
    action: 'Go to Brands and install',
  },
  kind: {
    web: 'Web chat',
    email: 'Email',
    whatsapp: 'WhatsApp',
    telegram: 'Telegram',
    other: 'Other',
  },
  status: {
    connected: 'Connected',
    needs_attention: 'Needs attention',
    paused: 'Paused',
  },
  problem: 'Chatwoot says: {{problem}}',
  web: {
    text: 'Your website’s chat widget.',
    link: 'Chat widget settings',
  },
  conversations_one: '{{count}} conversation this week',
  conversations_other: '{{count}} conversations this week',
  lastMessage: 'Last message: {{time}}',
  noMessages: 'No messages yet',
  ai: {
    label: 'AI answers here',
    hint: 'Off: your team answers every conversation. On: the AI answers first and hands over to your team when needed.',
    on: 'The AI now answers first on {{name}}.',
    off: 'Your team now answers every conversation on {{name}}.',
  },
  team: {
    label: 'Team',
    none: 'No team (use routing)',
    hidden: 'A team you can’t see',
    saved: 'New conversations on {{name}} now go to {{team}}.',
    cleared: 'Routing now decides who gets new conversations on {{name}}.',
  },
  pause: {
    action: 'Pause',
    label: 'Pause {{name}}',
    title: 'Pause {{name}}?',
    body: 'New conversations on this channel stay in Chatwoot until you resume. Conversations already here carry on.',
    confirm: 'Pause channel',
    done: '{{name}} is paused.',
  },
  resume: {
    action: 'Resume',
    label: 'Resume {{name}}',
    done: '{{name}} is running again.',
  },
  unclaimed: {
    text: 'Found in your Chatwoot account.',
    action: 'Use for this brand',
    done: '{{name}} now answers for {{brand}}.',
  },
  empty: {
    title: 'No email, WhatsApp or Telegram channels yet.',
    description:
      'Connect your support email, a WhatsApp Business number or a Telegram bot here. Channels added in Chatwoot appear when you check for new channels.',
  },
  check: {
    action: 'Check for new channels',
    busy: 'Checking…',
    found_one: 'Found {{count}} new channel',
    found_other: 'Found {{count}} new channels',
    none: 'No new channels',
  },
  email: {
    connect: {
      action: 'Connect email',
      title: 'Connect email',
      step: 'Step {{step}} of {{total}}',
      steps: {
        address: 'Your address',
        imap: 'Incoming mail (IMAP)',
        smtp: 'Outgoing mail (SMTP)',
        answering: 'Answering',
      },
      intros: {
        address: 'The address your customers write to.',
        imap: 'Chatwoot reads new emails from this server.',
        smtp: 'Your replies are sent from the same address through this server.',
        answering: 'Choose who answers emails here. You can change this later on the card.',
      },
      guide: 'How to connect email',
      back: 'Back',
      next: 'Next',
      submit: 'Connect',
      busy: 'Checking your mailbox…',
      busyHint: 'Chatwoot signs in to your mailbox before it connects it. This can take a moment.',
      done: 'Email connected. New emails will appear in your inbox.',
    },
    fields: {
      address: 'Support address',
      addressHint: 'For example help@yourshop.com.',
      name: 'Name in Studio',
      nameDefault: 'Support email',
      nameHint: 'Only your team sees this name.',
      provider: 'Email provider',
      providerNone: 'Choose your provider',
      custom: 'Another provider',
      find: 'Find my settings',
      found: 'We filled in the settings for {{provider}}.',
      unknown:
        'We don’t know the settings for this address. Ask your email provider for its IMAP and SMTP servers, and enter them in the next steps.',
      server: 'Server',
      imapServer: 'Incoming mail server',
      smtpServer: 'Outgoing mail server',
      port: 'Port',
      imapPort: 'Incoming port',
      smtpPort: 'Outgoing port',
      ssl: 'Use SSL',
      sslHint: 'Keep this on unless your provider says otherwise.',
      login: 'Login',
      imapLogin: 'Incoming login',
      smtpLogin: 'Outgoing login',
      loginHint: 'Usually your full email address.',
      imapPassword: 'Incoming password',
      smtpPassword: 'Outgoing password',
      security: 'Security',
      securityNone: 'None',
      noneHint:
        'Without security, your password is sent unprotected. Choose this only if your provider asks you to.',
      sameLogin: 'Same login and password as incoming',
      show: 'Show {{field}}',
      hide: 'Hide {{field}}',
    },
    appPassword: {
      title: 'Use an app password',
      text: 'Your provider asks for an app password instead of your normal password.',
      link: 'Make an app password',
    },
    microsoft: {
      title: 'Connect Microsoft in Chatwoot',
      text: 'Microsoft no longer accepts passwords for email apps. Connect Outlook or Microsoft 365 in Chatwoot (Settings → Inboxes → Email → Microsoft), then use Check for new channels.',
      blocked: 'Choose another provider to continue with a password.',
    },
    errors: {
      name: 'Enter a name, up to 100 characters.',
      address: 'Enter an email address, like help@yourshop.com.',
      server: 'Enter the server name, like mail.yourshop.com.',
      port: 'Enter a port from 1 to 65535.',
      login: 'Enter the login. It is usually your email address.',
      password: 'Enter the password.',
      newPassword: 'Enter the new password, or keep the current one.',
    },
    settings: {
      action: 'Email settings',
      label: 'Email settings for {{name}}',
      title: 'Email settings',
      loading: 'Loading email settings',
      general: 'Address',
      incoming: 'Incoming mail (IMAP)',
      outgoing: 'Outgoing mail (SMTP)',
      change: 'Change password',
      changeLabel: 'Change {{field}}',
      keep: 'Keep current password',
      kept: 'The password stays as it is.',
      save: 'Save',
      busy: 'Checking your mailbox…',
      done: 'Email settings saved.',
    },
    fix: {
      action: 'Fix sign-in',
      label: 'Fix sign-in for {{name}}',
      hint: 'Chatwoot can’t sign in to this mailbox. This usually happens after a password change. Enter the new password.',
    },
  },
  whatsapp: {
    connect: {
      action: 'Connect WhatsApp',
      title: 'Connect WhatsApp',
      steps: {
        number: 'Your number',
        answering: 'Answering',
        webhook: 'Set up Meta’s webhook',
      },
      intros: {
        number: 'Your WhatsApp Business number on Meta’s Cloud API.',
        answering:
          'Choose who answers WhatsApp messages here. You can change this later on the card.',
        webhook: 'Last step: tell Meta where to send your customers’ messages.',
      },
      guide: 'How to connect WhatsApp',
      submit: 'Connect',
      busy: 'Checking with Meta…',
      busyHint:
        'Chatwoot checks your access token with Meta before it connects the number. This can take a moment.',
      connected: 'Your WhatsApp number is connected. Now set up Meta’s webhook.',
      done: 'WhatsApp connected. Messages arrive in your inbox once Meta’s webhook is set up.',
    },
    fields: {
      nameDefault: 'WhatsApp',
      phoneNumber: 'Business phone number',
      phoneNumberHint: 'With + and the country code, for example +971 50 123 4567.',
      phoneNumberId: 'Phone number ID',
      businessAccountId: 'WhatsApp Business Account ID',
      accessToken: 'Access token',
      accessTokenHint: 'It goes straight to Chatwoot. Studio doesn’t keep it.',
    },
    help: {
      title: 'Where to find these',
      text: 'Find these in Meta’s WhatsApp Manager → API setup. Use a permanent access token from a system user.',
      link: 'Meta’s Cloud API guide',
    },
    errors: {
      phoneNumber: 'Enter the number with + and the country code, like +971501234567.',
      phoneNumberId: 'Enter the Phone number ID. It has digits only.',
      businessAccountId: 'Enter the WhatsApp Business Account ID. It has digits only.',
      accessToken: 'Enter the access token. It is at least 20 characters long.',
    },
    setup: {
      action: 'Meta set-up',
      label: 'Meta set-up for {{name}}',
      title: 'Set up Meta’s webhook',
      loading: 'Loading Meta’s set-up',
      instructions:
        'In Meta’s App Dashboard → WhatsApp → Configuration → Webhook, paste these, choose Verify and save, then subscribe to messages.',
      callbackUrl: 'Callback URL',
      verifyToken: 'Verify token',
      copy: 'Copy',
      copyLabel: 'Copy {{field}}',
      noToken: 'Chatwoot hasn’t made a verify token yet. Open Meta set-up again in a moment.',
      done: 'Done',
    },
  },
  telegram: {
    connect: {
      action: 'Connect Telegram',
      title: 'Connect Telegram',
      intro: 'Connect your Telegram bot. Messages people send to the bot arrive in your inbox.',
      guide: 'How to connect Telegram',
      submit: 'Connect',
      busy: 'Checking with Telegram…',
      done: 'Telegram connected as {{bot}}. New messages will appear in your inbox.',
      doneUnnamed: 'Telegram connected. New messages will appear in your inbox.',
    },
    fields: {
      nameDefault: 'Telegram',
      botToken: 'Bot token',
      botTokenHint: 'It goes straight to Chatwoot. Studio doesn’t keep it.',
    },
    help: {
      title: 'Get a bot token',
      text: 'Message @BotFather on Telegram, send /newbot and copy the token it gives you.',
      link: 'Telegram’s guide to BotFather',
    },
    errors: {
      botToken: 'Enter the token from @BotFather. It looks like 123456789:AAH… with no spaces.',
    },
  },
  reply: {
    email: 'Your reply is sent by email.',
    whatsapp: 'Your reply is sent on WhatsApp.',
    telegram: 'Your reply is sent on Telegram.',
    minutesLeft_one: 'You can reply for another minute.',
    minutesLeft_other: 'You can reply for another {{count}} minutes.',
    closed:
      'WhatsApp only allows template messages now. You can reply again after the customer writes.',
  },
  templates: {
    button: 'Template',
    open: 'Send a template',
    title: 'Send a WhatsApp template',
    description:
      'After 24 hours without a message from the customer, WhatsApp delivers only templates Meta approved.',
    loading: 'Loading templates',
    search: 'Search templates',
    category: {
      UTILITY: 'Utility',
      MARKETING: 'Marketing',
      AUTHENTICATION: 'Authentication',
      other: 'Other',
    },
    unsupported: {
      not_approved: 'Waiting for Meta’s approval',
      authentication: 'One-time codes are sent by your own systems',
      media_header: 'Templates with a photo, video or document header can’t be sent from here yet',
    },
    synced: 'Templates last updated {{time}}.',
    syncHint: 'New templates appear here after Chatwoot syncs them from Meta.',
    empty: {
      title: 'No approved templates yet',
      description:
        'Create one in Meta’s WhatsApp Manager. It appears here once Meta approves it and Chatwoot syncs it.',
      guide: 'How template messages work',
    },
    noMatch: 'No template matches “{{query}}”.',
    back: 'All templates',
    fields: {
      title: 'Fill in',
      none: 'This template has nothing to fill in.',
      header: 'Header {{variable}}',
      body: 'Message {{variable}}',
      button: 'Button “{{label}}”',
      prefilled: 'Filled in from this conversation.',
      code: 'The code, up to 15 characters.',
      url: 'Added to the end of the button’s link.',
    },
    preview: 'Preview',
    send: 'Send template',
    missing_one: 'Fill in one more value to send.',
    missing_other: 'Fill in {{count}} more values to send.',
    errors: {
      required: 'Fill this in.',
      code: 'A code can have at most 15 characters.',
    },
    sent: 'Template sent.',
  },
  chatwoot: {
    title: 'Chatwoot didn’t answer',
    description: 'Try again in a moment.',
  },
};
